import React, { useState } from 'react';
import { Card, Badge, PrimaryButton, GhostButton, Field, TextArea, StringListEditor, ArrayEditor } from './dprFields';
import { generateNarrative, updateCase } from './dprApi';
import { exportFullDPR } from './dprExport';

const Sparkle = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>;

function Chapter({ number, title, children }) {
  return (
    <Card className="p-5 mb-4">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-6 h-6 rounded-full bg-orange-500/10 dark:bg-orange-500/15 text-[#ff5200] dark:text-orange-300 text-[11px] font-extrabold flex items-center justify-center flex-shrink-0">{number}</span>
        <h3 className="text-sm font-extrabold text-slate-800 dark:text-slate-100">{title}</h3>
      </div>
      {children}
    </Card>
  );
}

export default function NarrativeReport({ caseId, caseData, setCaseData }) {
  const narrative = caseData.narrative_sections || {};
  const hasNarrative = narrative && Object.keys(narrative).length > 0;
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const setN = (key, value) => setCaseData(c => ({ ...c, narrative_sections: { ...(c.narrative_sections || {}), [key]: value } }));
  const setSwot = (key, value) => setCaseData(c => ({ ...c, narrative_sections: { ...(c.narrative_sections || {}), swot: { ...((c.narrative_sections || {}).swot || {}), [key]: value } } }));
  const setPestel = (key, value) => setCaseData(c => ({ ...c, narrative_sections: { ...(c.narrative_sections || {}), pestel: { ...((c.narrative_sections || {}).pestel || {}), [key]: value } } }));

  const handleGenerate = async () => {
    setGenerating(true); setError(null);
    try {
      const updated = await generateNarrative(caseId);
      setCaseData(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const updated = await updateCase(caseId, caseData);
      setCaseData(updated);
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-extrabold text-slate-800 dark:text-slate-100">Narrative DPR Chapters</h2>
          <p className="text-xs text-slate-400 mt-0.5">AI-drafted from your project data — review and edit every section before this goes to a bank.</p>
        </div>
        <div className="flex items-center gap-2">
          {savedAt && <span className="text-xs text-emerald-500">Saved {savedAt.toLocaleTimeString()}</span>}
          {hasNarrative && <GhostButton onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Edits'}</GhostButton>}
          {hasNarrative && <GhostButton onClick={() => exportFullDPR(caseData, caseData.computed_schedules)}>⬇ Download Full DPR (Word)</GhostButton>}
          <PrimaryButton onClick={handleGenerate} disabled={generating}>
            <span className="flex items-center gap-1.5">{Sparkle} {generating ? 'Drafting…' : hasNarrative ? 'Regenerate with AI' : 'Generate with AI'}</span>
          </PrimaryButton>
        </div>
      </div>

      {error && <Card className="p-3 mb-4 border-rose-200 bg-rose-50 dark:bg-rose-500/10"><p className="text-xs text-rose-600">{error}</p></Card>}

      {!hasNarrative && !generating && (
        <Card className="p-8 text-center">
          <p className="text-sm text-slate-500 mb-1">No narrative drafted yet.</p>
          <p className="text-xs text-slate-400 mb-4">This uses your saved Business Profile and computed CMA numbers to draft all 9 chapters in one pass — you'll review and edit everything after.</p>
          <PrimaryButton onClick={handleGenerate} disabled={generating}>
            <span className="flex items-center gap-1.5">{Sparkle} Generate with AI</span>
          </PrimaryButton>
        </Card>
      )}

      {generating && (
        <Card className="p-8 text-center">
          <p className="text-sm text-slate-500">Drafting your project brief, industry overview, SWOT, assumptions and more…</p>
        </Card>
      )}

      {hasNarrative && (
        <>
          <Chapter number={1} title="Project Brief">
            <TextArea value={narrative.projectBrief} onChange={v => setN('projectBrief', v)} rows={4} />
          </Chapter>
          <Chapter number={2} title="Industry Introduction">
            <TextArea value={narrative.industryIntroduction} onChange={v => setN('industryIntroduction', v)} rows={5} />
          </Chapter>
          <Chapter number={3} title="About Us — Promoter Background">
            <TextArea value={narrative.aboutUs} onChange={v => setN('aboutUs', v)} rows={3} />
          </Chapter>
          <Chapter number={4} title="Manufacturing / Service Process">
            <TextArea value={narrative.manufacturingProcess} onChange={v => setN('manufacturingProcess', v)} rows={4} />
          </Chapter>
          <Chapter number={5} title="PESTEL Analysis">
            <div className="grid grid-cols-2 gap-x-4">
              {['political', 'economic', 'social', 'technological', 'ecological', 'legal'].map(k => (
                <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
                  <TextArea value={(narrative.pestel || {})[k]} onChange={v => setPestel(k, v)} rows={2} />
                </Field>
              ))}
            </div>
          </Chapter>
          <Chapter number={6} title="SWOT Analysis">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Strengths"><StringListEditor items={(narrative.swot || {}).strengths} onChange={v => setSwot('strengths', v)} /></Field>
              <Field label="Weaknesses"><StringListEditor items={(narrative.swot || {}).weaknesses} onChange={v => setSwot('weaknesses', v)} /></Field>
              <Field label="Opportunities"><StringListEditor items={(narrative.swot || {}).opportunities} onChange={v => setSwot('opportunities', v)} /></Field>
              <Field label="Threats"><StringListEditor items={(narrative.swot || {}).threats} onChange={v => setSwot('threats', v)} /></Field>
            </div>
          </Chapter>
          <Chapter number={7} title="Implementation Schedule">
            <ArrayEditor rows={narrative.implementationSchedule} columns={[{ key: 'activity', label: 'Activity' }, { key: 'targetPeriod', label: 'Target Period' }]}
              onChange={rows => setN('implementationSchedule', rows)} addLabel="+ Add Activity" />
          </Chapter>
          <Chapter number={8} title="Resource Requirements">
            <TextArea value={narrative.resourceRequirements} onChange={v => setN('resourceRequirements', v)} rows={4} />
          </Chapter>
          <Chapter number={9} title="Assumptions">
            <StringListEditor items={narrative.assumptions} onChange={v => setN('assumptions', v)} placeholder="Add an assumption…" />
          </Chapter>
          <Chapter number={10} title="Conclusion">
            <TextArea value={narrative.conclusion} onChange={v => setN('conclusion', v)} rows={3} />
          </Chapter>
          {narrative.financialAppraisal && (
            <Chapter number={11} title="Independent Financial Appraisal & Risk Assessment">
              <Badge tone="indigo">System-generated from your computed CMA numbers — read by a bank credit officer</Badge>
              <div className="mt-3 space-y-3">
                <Field label="Capital Structure & Funding"><TextArea value={narrative.financialAppraisal.capitalStructureFunding} onChange={v => setN('financialAppraisal', { ...narrative.financialAppraisal, capitalStructureFunding: v })} rows={2} /></Field>
                <Field label="Debt-Servicing Capacity"><TextArea value={narrative.financialAppraisal.debtServicingCapacity} onChange={v => setN('financialAppraisal', { ...narrative.financialAppraisal, debtServicingCapacity: v })} rows={2} /></Field>
                <Field label="Profitability & Returns"><TextArea value={narrative.financialAppraisal.profitabilityReturns} onChange={v => setN('financialAppraisal', { ...narrative.financialAppraisal, profitabilityReturns: v })} rows={2} /></Field>
                <Field label="Liquidity & Working Capital"><TextArea value={narrative.financialAppraisal.liquidityWorkingCapital} onChange={v => setN('financialAppraisal', { ...narrative.financialAppraisal, liquidityWorkingCapital: v })} rows={2} /></Field>
                <Field label="Break-Even & Resilience"><TextArea value={narrative.financialAppraisal.breakEvenResilience} onChange={v => setN('financialAppraisal', { ...narrative.financialAppraisal, breakEvenResilience: v })} rows={1} /></Field>
                <Field label="Risk Observations"><StringListEditor items={narrative.financialAppraisal.riskObservations} onChange={v => setN('financialAppraisal', { ...narrative.financialAppraisal, riskObservations: v })} /></Field>
                <Field label="Overall Assessment"><TextArea value={narrative.financialAppraisal.overallAssessment} onChange={v => setN('financialAppraisal', { ...narrative.financialAppraisal, overallAssessment: v })} rows={3} /></Field>
              </div>
              <p className="text-[11px] text-slate-400 mt-3 italic">{narrative.appraisalDisclaimer}</p>
            </Chapter>
          )}
          <Chapter number={12} title="Disclaimer">
            <Badge tone="amber">Standard boilerplate — edit only if your firm uses different wording</Badge>
            <div className="mt-2"><TextArea value={narrative.disclaimer} onChange={v => setN('disclaimer', v)} rows={3} /></div>
          </Chapter>
        </>
      )}
    </div>
  );
}
